"use client";
import {
  Dialog,
  DialogContent,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
// import '@fontsource/poppins';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import { updateFirstLetter } from "utils/helper";
import { htmlToText } from "html-to-text";
import SuiButton from "components/SuiButton";

export default function RecipeDetailModal({
  open,
  onClose,
  recipe,
  onAdd,
  onEdit,
  onDelete,
}) {
  if (!recipe) return null;

  const ingredients = Array.isArray(recipe.ingredients)
    ? recipe.ingredients.join(", ")
    : recipe.ingredients ?? "";
  const groceryList = Array.isArray(recipe.groceryList)
    ? recipe.groceryList.join(", ")
    : recipe.groceryList ?? "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "10px",
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: "relative", my: 2 }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            bgcolor: "#fff",
            // boxShadow: 2,
            zIndex: 1,
            "&:hover": { bgcolor: "grey.100" },
            mb: 2,
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Header Section with Image and Overlay */}
        <SuiBox
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 4,
            p: 3,
            alignItems: "stretch", // make both columns stretch equally
          }}
        >
          <SuiBox
            sx={{
              position: "relative",
              height: { xs: "auto", md: "100%" },
              display: "flex",
              flexDirection: "column",
            }}
          >
            {recipe.image ? (
              <SuiBox
                component="img"
                src={recipe.image}
                alt={recipe.name}
                sx={{
                  width: "100%",
                  height: "300px",
                  objectFit: "cover",
                  borderRadius: 2,
                }}
              />
            ) : (
              <SuiBox
                sx={{
                  width: "100%",
                  height: "100%",
                  backgroundColor: "#f0f0f0",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "120px",
                  fontWeight: 700,
                  color: "#888",
                  textTransform: "uppercase",
                }}
              >
                {recipe.name
                  .split(" ")
                  .slice(0, 2)
                  .map((word) => word.charAt(0))
                  .join("")}
              </SuiBox>
            )}

            {/* Calorie & Time Overlay */}
            <SuiBox
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                bgcolor: "#fff",
                py: 1,
                px: 2,
                borderTopLeftRadius: 20,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <SuiTypography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  color: "#003264",
                }}
              >
                🔥 {recipe.calories} Cal
              </SuiTypography>
              <SuiTypography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  color: "#003264",
                }}
              >
                ⏱ {recipe.time || "20 min"}
              </SuiTypography>
            </SuiBox>
          </SuiBox>

          {/* Right - Content Section */}
          <SuiBox
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between", // make content stretch evenly
              height: "100%", // match image height
            }}
          >
            <SuiTypography
              variant="h5"
              sx={{
                fontWeight: 700,
                textTransform: "capitalize",
              }}
            >
              {recipe.name}
            </SuiTypography>
            <SuiTypography
              variant="h3"
              sx={{
                color: "#ff6b35",
                fontWeight: 600,
                mb: 2,
                fontSize: "28px",
              }}
            >
              {recipe.calories} kcal
            </SuiTypography>

            <SuiTypography
              variant="h6"
              sx={{
                fontWeight: 500,
                mb: 1,
                color: "#003264",
              }}
            >
              Macro/Micro Nutrient Breakdown
            </SuiTypography>

            <SuiBox
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                bgcolor: "#f9f9f9",
                borderRadius: 2,
                p: 2,
              }}
            >
              {Array.isArray(recipe?.nutritions) &&
              recipe.nutritions.length > 0 ? (
                recipe.nutritions
                  .map((macro) => {
                    // If already structured
                    if (
                      typeof macro === "object" &&
                      macro !== null &&
                      "type" in macro &&
                      "grams" in macro &&
                      "unit" in macro
                    ) {
                      return macro;
                    }

                    // If string like "Protein:7.17g"
                    if (typeof macro === "string" && macro.includes(":")) {
                      const [type, valueWithUnit] = macro.split(":");
                      const valueMatch = valueWithUnit.match(
                        /^([\d.]+)([a-zA-Z]+)$/
                      );
                      if (valueMatch) {
                        const [, grams, unit] = valueMatch;
                        return {
                          type: type.trim(),
                          grams,
                          unit,
                        };
                      }
                    }

                    // Invalid format, skip
                    return null;
                  })
                  .filter(Boolean)
                  .map((macro, index) => (
                    <SuiBox
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.5,
                        borderBottom:
                          index !== recipe.nutritions.length - 1
                            ? "1px solid #e0e0e0"
                            : "none",
                      }}
                    >
                      <SuiTypography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "#000000" }}
                      >
                        {updateFirstLetter(macro.type)}
                      </SuiTypography>
                      <SuiTypography variant="body2">
                        {macro.grams}
                        {macro.unit}
                      </SuiTypography>
                    </SuiBox>
                  ))
              ) : (
                <SuiTypography
                  variant="body2"
                  sx={{ fontWeight: 500, color: "#000000" }}
                >
                  No Data
                </SuiTypography>
              )}
            </SuiBox>

            {/* Actions */}
            <SuiBox
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
                mt: 2,
              }}
            >
              <SuiButton
                component={Button}
                color="dark"
                variant="gradient"
                size="small"
                startIcon={<span style={{ fontSize: 18 }}>+</span>}
                onClick={() => onAdd(recipe)}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  minWidth: "fit-content",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                Add
              </SuiButton>

              {onEdit && (
                <Tooltip title="Edit this recipe permanently." arrow>
                  <IconButton
                    onClick={() => onEdit(recipe)}
                    sx={{
                      bgcolor: "#FFCDA9",
                      color: "#000",
                      borderRadius: 1,
                      "&:hover": { bgcolor: "grey.200" },
                    }}
                  >
                    <EditIcon sx={{ color: "#000" }} />
                  </IconButton>
                </Tooltip>
              )}

              {onDelete && (
                <Tooltip
                  title="This action will permanently delete the recipe."
                  arrow
                >
                  <IconButton
                    onClick={() => onDelete(recipe.id)}
                    sx={{
                      bgcolor: "#FFCDA9",
                      color: "#000",
                      borderRadius: 1,
                      "&:hover": { bgcolor: "grey.200" },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </SuiBox>
          </SuiBox>
        </SuiBox>

        {/* Recipe Details */}
        <SuiBox sx={{ mb: 2, px: 3, py: 0 }}>
          <SuiTypography
            variant="h6"
            sx={{ fontWeight: 500, color: "#003264", fontSize: "16px" }}
          >
            Recipe Details
          </SuiTypography>

          <SuiTypography
            variant="body1"
            sx={{
              lineHeight: 1.6,
              textTransform: "capitalize",
              fontSize: "14px",
            }}
          >
            {htmlToText(recipe?.preparation)}
          </SuiTypography>
        </SuiBox>

        {/* Ingredients */}
        <SuiBox sx={{ px: 3, py: 0 }}>
          <SuiTypography
            variant="h6"
            sx={{ fontWeight: 500, color: "#003264", fontSize: "16px" }}
          >
            Ingredients
          </SuiTypography>

          <SuiTypography
            variant="body1"
            sx={{
              lineHeight: 1.6,
              textTransform: "capitalize",
              fontSize: "14px",
            }}
          >
            {ingredients}
          </SuiTypography>
        </SuiBox>

        {groceryList ? (
          <SuiBox sx={{ px: 3, py: 1 }}>
            <SuiTypography
              variant="h6"
              sx={{ fontWeight: 500, color: "#003264", fontSize: "16px" }}
            >
              Grocery List
            </SuiTypography>
            <SuiTypography
              variant="body1"
              sx={{
                lineHeight: 1.6,
                textTransform: "capitalize",
                fontSize: "14px",
              }}
            >
              {groceryList}
            </SuiTypography>
          </SuiBox>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
